import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { motion } from "framer-motion";
import { FileText, Landmark, Pencil, Trash2 } from "lucide-react";
import { springs } from "@/components/ui/motion";
import { Button } from "@/components/ui/primitives";
import { ClauseRefBadge } from "../ClauseRefBadge";
import type { UIChecklistItem } from "@/packages/types";

interface ChecklistItemRowProps {
  item: UIChecklistItem;
  index: number;
  onUpdate: (itemId: string, updates: Partial<UIChecklistItem>) => void;
  onDelete: (itemId: string) => void;
}

export function ChecklistItemRow({
  item,
  index,
  onUpdate,
  onDelete,
}: ChecklistItemRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.name);
  const [editDescription, setEditDescription] = useState(
    item.description ?? "",
  );
  const [editRequired, setEditRequired] = useState(item.required);

  function handleEdit() {
    setEditTitle(item.name);
    setEditDescription(item.description ?? "");
    setEditRequired(item.required);
    setIsEditing(true);
  }

  function handleSave() {
    onUpdate(item.id, {
      name: editTitle.trim(),
      description: editDescription.trim(),
      required: editRequired,
    });
    setIsEditing(false);
  }

  function handleCancel() {
    setIsEditing(false);
  }

  const clauseRefs = item.clauseDetails?.map((c) => c.clauseNumber) ?? [];

  return (
    <Popover.Root
      onOpenChange={(open) => {
        if (!open) setIsEditing(false);
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 hover:bg-card/30 transition-colors cursor-pointer text-left"
        >
          <motion.div
            className="h-2 w-2 shrink-0 rounded-full bg-muted"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ ...springs.bouncy, delay: index * 0.03 }}
          />
          <span className="text-[13px] font-medium text-primary truncate">
            {item.name}
          </span>
          {item.required && (
            <span className="shrink-0 rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent">
              required
            </span>
          )}
          {clauseRefs.length > 0 && (
            <span className="ml-auto shrink-0 text-[10px] text-muted">
              {clauseRefs.length} clause
              {clauseRefs.length > 1 ? "s" : ""}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="z-50 w-[340px] overflow-hidden rounded-[10px] bg-card border border-border shadow-xl"
        >
          <div className="h-[3px] bg-accent" />

          <div className="p-4 space-y-3">
            {isEditing ? (
              <EditForm
                title={editTitle}
                description={editDescription}
                required={editRequired}
                onTitleChange={setEditTitle}
                onDescriptionChange={setEditDescription}
                onRequiredChange={setEditRequired}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            ) : (
              <ViewContent
                item={item}
                onEdit={handleEdit}
                onDelete={onDelete}
              />
            )}
          </div>

          <Popover.Arrow className="fill-card" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ─── View Mode Content ──────────────────────────────────────────────────────

function ViewContent({
  item,
  onEdit,
  onDelete,
}: {
  item: UIChecklistItem;
  onEdit: () => void;
  onDelete: (itemId: string) => void;
}) {
  return (
    <>
      <div>
        <p className="text-[13px] font-semibold text-primary leading-snug">
          {item.name}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          {item.required && (
            <span className="rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent">
              required
            </span>
          )}
        </div>
      </div>

      {item.description && (
        <>
          <div className="h-px bg-border" />
          <p className="text-[12px] text-secondary leading-relaxed">
            {item.description}
          </p>
        </>
      )}

      {item.exampleDocuments && item.exampleDocuments.length > 0 && (
        <>
          <div className="h-px bg-border" />
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <FileText size={11} className="text-muted" />
              <span className="text-[10px] font-semibold text-muted uppercase tracking-[0.06em]">
                Expected Evidence
              </span>
            </div>
            <div className="space-y-1">
              {item.exampleDocuments.map((doc) => (
                <p key={doc.name} className="text-[11px] text-secondary pl-4">
                  {doc.name}
                </p>
              ))}
            </div>
          </div>
        </>
      )}

      {item.clauseDetails && item.clauseDetails.length > 0 && (
        <>
          <div className="h-px bg-border" />
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Landmark size={11} className="text-muted" />
              <span className="text-[10px] font-semibold text-muted uppercase tracking-[0.06em]">
                Clause References
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {item.clauseDetails.map((ci) => (
                <ClauseRefBadge
                  key={ci.clauseNumber}
                  clauseRef={ci.clauseNumber}
                  clauseInfo={{
                    standard: "",
                    title: ci.title,
                    description: ci.description ?? "",
                  }}
                />
              ))}
            </div>
          </div>
        </>
      )}

      <div className="h-px bg-border" />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium text-secondary hover:text-primary hover:bg-card-hover transition-colors"
        >
          <Pencil size={12} />
          Edit
        </button>
        <Popover.Close asChild>
          <button
            type="button"
            onClick={() => onDelete(item.id)}
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-colors"
          >
            <Trash2 size={12} />
            Remove
          </button>
        </Popover.Close>
      </div>
    </>
  );
}

// ─── Edit Mode Form ─────────────────────────────────────────────────────────

function EditForm({
  title,
  description,
  required,
  onTitleChange,
  onDescriptionChange,
  onRequiredChange,
  onSave,
  onCancel,
}: {
  title: string;
  description: string;
  required: boolean;
  onTitleChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onRequiredChange: (v: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <div>
        <label
          htmlFor="edit-item-title"
          className="text-[10px] font-semibold text-muted uppercase tracking-wide"
        >
          Title
        </label>
        <input
          id="edit-item-title"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
          className="mt-1 w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] text-primary outline-none focus:border-accent/50 transition-colors"
        />
      </div>

      <div>
        <label
          htmlFor="edit-item-description"
          className="text-[10px] font-semibold text-muted uppercase tracking-wide"
        >
          Description
        </label>
        <textarea
          id="edit-item-description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onCancel();
          }}
          rows={3}
          className="mt-1 w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[12px] text-primary outline-none focus:border-accent/50 transition-colors resize-none"
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={required}
          onChange={(e) => onRequiredChange(e.target.checked)}
          className="rounded border-border accent-accent"
        />
        <span className="text-[12px] text-secondary">Required item</span>
      </label>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={title.trim() === ""}
        >
          Save
        </Button>
      </div>
    </>
  );
}
